import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Paper,
  Typography,
  Button,
} from '@mui/material';

const LANGUAGE_LEVELS = [
  { value: '', label: '-' },
  { value: 'S', label: 'S (Sedang)' },
  { value: 'CB', label: 'CB (Cukup Baik)' },
  { value: 'B', label: 'B (Baik)' },
  { value: 'SB', label: 'SB (Sangat Baik)' },
];

const POPULAR_LANGUAGES = [
  { value: '', label: 'Pilih Bahasa' },
  { value: 'Bahasa Indonesia', label: 'Bahasa Indonesia' },
  { value: 'Inggris', label: 'Inggris' },
  { value: 'Jepang', label: 'Jepang' },
  { value: 'Prancis', label: 'Prancis' },
  { value: 'China', label: 'China (Mandarin)' },
  { value: 'Arab', label: 'Arab' },
  { value: 'Jerman', label: 'Jerman' },
  { value: 'Korea', label: 'Korea' },
  { value: 'Spanyol', label: 'Spanyol' },
  { value: 'Italia', label: 'Italia' },
  { value: 'Belanda', label: 'Belanda' },
  { value: 'Rusia', label: 'Rusia' },
  { value: 'Thailand', label: 'Thailand' },
  { value: 'Vietnam', label: 'Vietnam' },
  { value: 'Lainnya', label: 'Lainnya' },
];

const Step4Bahasa = ({ form, setForm }) => {
  const [internalLanguages, setInternalLanguages] = useState(() => {
    return Array.isArray(form.languages) ? form.languages : [{ language: '', written: '', oral: '', remark: '' }];
  });

  // Sync internal state with parent form state
  useEffect(() => {
    setForm(prev => ({
      ...prev,
      languages: internalLanguages
    }));
  }, [internalLanguages, setForm]);

  // Helper: update array item by index/field
  const handleArrayChange = (index, field, value) => {
    setInternalLanguages(prev => {
      const current = Array.isArray(prev) ? prev : [];
      const newArray = [...current];
      newArray[index] = { ...newArray[index], [field]: value };
      return newArray;
    });
  };

  const handleArrayRemove = (index) => {
    setInternalLanguages(prev => {
      const current = Array.isArray(prev) ? prev : [];
      return current.filter((_, i) => i !== index);
    });
  };

  const addLanguage = () => {
    setInternalLanguages(prev => {
      const current = Array.isArray(prev) ? prev : [];
      return [...current, { language: '', written: '', oral: '', remark: '' }];
    });
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', my: 4, p: 2 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          backgroundColor: '#ffffff', 
          mb: 3,
          border: '2px solid #1976d2',
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(25, 118, 210, 0.15)'
        }}
      >
        <Grid container spacing={3}>
          {/* Header Section */}
          <Grid item xs={12}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                pb: 2,
                borderBottom: '2px solid #e3f2fd'
              }}
            >
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 'bold', 
                  color: '#1976d2',
                  fontSize: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <span>🌍</span>
                Data Bahasa
              </Typography>
            </Box>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#666', 
                mb: 3,
                fontStyle: 'italic',
                fontSize: '0.95rem'
              }}
            >
              Silakan isi data kemampuan bahasa yang Anda kuasai. Klik "Tambah Bahasa" untuk menambahkan bahasa lainnya.
            </Typography>
          </Grid>

          {/* Languages Table */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#495057' }}>
                Kemampuan Bahasa
              </Typography>
              
              {/* Table Header */}
              <Grid container spacing={1} sx={{ mb: 2, fontWeight: 'bold', backgroundColor: '#e3f2fd', p: 1, borderRadius: 1 }}>
                <Grid item xs={1}>No</Grid>
                <Grid item xs={3}>Bahasa</Grid>
                <Grid item xs={2}>Tertulis</Grid>
                <Grid item xs={2}>Lisan</Grid>
                <Grid item xs={3}>Keterangan</Grid>
                <Grid item xs={1}>Aksi</Grid>
              </Grid>
              
              {/* Table Rows */}
              {internalLanguages.map((row, idx) => (
                <Grid container spacing={1} key={idx} sx={{ mb: 1, alignItems: 'center', p: 1, backgroundColor: '#ffffff', borderRadius: 1 }}>
                  <Grid item xs={1}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{idx + 1}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <FormControl fullWidth size="small" required>
                      <Select 
                        value={row.language || ''} 
                        onChange={(e) => handleArrayChange(idx, 'language', e.target.value)}
                        displayEmpty
                        required
                        sx={{ 
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            '&:hover fieldset': {
                              borderColor: '#1976d2',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#1976d2',
                            },
                          },
                        }}
                      >
                        {POPULAR_LANGUAGES.map((language) => (
                          <MenuItem key={language.value} value={language.value}>{language.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="caption" sx={{ color: '#666', mb: 0.5, display: 'block' }}>
                      Level Kemampuan Menulis
                    </Typography>
                    <FormControl fullWidth size="small" required>
                      <Select 
                        value={row.written || ''} 
                        onChange={(e) => handleArrayChange(idx, 'written', e.target.value)}
                        displayEmpty
                        required
                        sx={{ 
                          minWidth: 150,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            '&:hover fieldset': {
                              borderColor: '#1976d2',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#1976d2',
                            },
                          },
                        }}
                      >
                        {LANGUAGE_LEVELS.map((level) => (
                          <MenuItem key={level.value} value={level.value}>{level.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="caption" sx={{ color: '#666', mb: 0.5, display: 'block' }}>
                      Level Kemampuan Berbicara
                    </Typography>
                    <FormControl fullWidth size="small" required>
                      <Select 
                        value={row.oral || ''} 
                        onChange={(e) => handleArrayChange(idx, 'oral', e.target.value)}
                        displayEmpty
                        required
                        sx={{ 
                          minWidth: 150,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            '&:hover fieldset': {
                              borderColor: '#1976d2',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#1976d2',
                            },
                          },
                        }}
                      >
                        {LANGUAGE_LEVELS.map((level) => (
                          <MenuItem key={level.value} value={level.value}>{level.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField 
                      placeholder="Keterangan"
                      value={row.remark} 
                      onChange={(e) => handleArrayChange(idx, 'remark', e.target.value)} 
                      fullWidth 
                      size="small"
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <Button 
                      size="small" 
                      onClick={() => handleArrayRemove(idx)}
                      variant="outlined"
                      color="error"
                      disabled={internalLanguages.length === 1}
                      sx={{ minWidth: 60 }}
                    >
                      Hapus
                    </Button>
                  </Grid>
                </Grid>
              ))}
              
              <Button 
                type="button" 
                size="small" 
                onClick={addLanguage}
                variant="contained"
                sx={{ 
                  mt: 2,
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  }
                }}
              >
                + Tambah Bahasa
              </Button>
            </Paper>
          </Grid>

          {/* Language Proficiency Summary - Hidden */}
          <Grid item xs={12} sx={{ display: 'none' }}>
            <Paper elevation={1} sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#495057' }}>
                Ringkasan Kemampuan Bahasa
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField 
                    label="Bahasa Utama" 
                    name="primaryLanguage" 
                    value={form.primaryLanguage || ''} 
                    onChange={(e) => setForm(prev => ({ ...prev, primaryLanguage: e.target.value }))}
                    fullWidth 
                    size="medium"
                    placeholder="Contoh: Bahasa Indonesia"
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        fontWeight: 500,
                        color: '#555',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    label="Bahasa Asing Terkuat" 
                    name="strongestForeignLanguage" 
                    value={form.strongestForeignLanguage || ''} 
                    onChange={(e) => setForm(prev => ({ ...prev, strongestForeignLanguage: e.target.value }))}
                    fullWidth 
                    size="medium"
                    placeholder="Contoh: Bahasa Inggris"
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        fontWeight: 500,
                        color: '#555',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    label="Pengalaman Menggunakan Bahasa Asing" 
                    name="foreignLanguageExperience" 
                    value={form.foreignLanguageExperience || ''} 
                    onChange={(e) => setForm(prev => ({ ...prev, foreignLanguageExperience: e.target.value }))}
                    fullWidth 
                    size="medium"
                    placeholder="Jelaskan pengalaman Anda menggunakan bahasa asing dalam pekerjaan atau kehidupan sehari-hari"
                    variant="outlined"
                    multiline
                    rows={3}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        fontWeight: 500,
                        color: '#555',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    label="Sertifikasi Bahasa" 
                    name="languageCertifications" 
                    value={form.languageCertifications || ''} 
                    onChange={(e) => setForm(prev => ({ ...prev, languageCertifications: e.target.value }))}
                    fullWidth 
                    size="medium"
                    placeholder="Contoh: TOEFL 550, IELTS 6.5, TOEIC 800"
                    variant="outlined"
                    multiline
                    rows={2}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        fontWeight: 500,
                        color: '#555',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Communication Skills */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#495057' }}>
                Kemampuan Komunikasi
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="medium" required>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#555', mb: 1 }}>
                      Kemampuan Presentasi
                    </Typography>
                    <Select 
                      name="presentationSkills"
                      value={form.presentationSkills || ''} 
                      onChange={(e) => setForm(prev => ({ ...prev, presentationSkills: e.target.value }))}
                      displayEmpty
                      required
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    >
                      {LANGUAGE_LEVELS.map((level) => (
                        <MenuItem key={level.value} value={level.value}>{level.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="medium" required>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#555', mb: 1 }}>
                      Kemampuan Negosiasi
                    </Typography>
                    <Select 
                      name="negotiationSkills"
                      value={form.negotiationSkills || ''} 
                      onChange={(e) => setForm(prev => ({ ...prev, negotiationSkills: e.target.value }))}
                      displayEmpty
                      required
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: '#1976d2',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1976d2',
                          },
                        },
                      }}
                    >
                      {LANGUAGE_LEVELS.map((level) => (
                        <MenuItem key={level.value} value={level.value}>{level.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField 
                    label="Kemampuan Menulis" 
                    name="writingSkills" 
                    value={form.writingSkills || ''} 
                    onChange={(e) => setForm(prev => ({ ...prev, writingSkills: e.target.value }))}
                    fullWidth 
                    size="medium"
                    placeholder="Jelaskan kemampuan menulis Anda (laporan, email, dokumen teknis, dll)"
                    variant="outlined"
                    multiline
                    rows={4}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        fontWeight: 500,
                        color: '#555',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Step4Bahasa;
